// Server Component - static content rendered on server
"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Footer } from "~/components/footer";
import { WaitlistDialog } from "~/components/waitlist-dialog";
import Cookies from "js-cookie";
import {
  Zap,
  RefreshCcw,
  Brain,
  Target,
  BarChart3,
  Check,
  ArrowRight,
  Clock,
  DollarSign,
  Layers,
  Sparkles,
  Calendar,
  TrendingUp,
  Menu,
  X,
} from "lucide-react";

export default function LandingPage() {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [isWaitlistMember, setIsWaitlistMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check cookie on mount
    const joined = Cookies.get("waitlistJoined");
    setIsWaitlistMember(!!joined);
    setIsLoading(false);
  }, []);

  const problems = [
    {
      icon: Clock,
      title: "Creative Fatigue",
      description: "60% of ads stop working within 30 days. By the time you notice, you've wasted thousands.",
    },
    {
      icon: DollarSign,
      title: "Slow Testing",
      description: "Manual A/B testing takes 2-4 weeks to find winners. Your budget bleeds while you wait.",
    },
    {
      icon: Layers,
      title: "Platform Chaos",
      description: "What works on Meta fails on TikTok. Managing each platform manually is impossible at scale.",
    },
    {
      icon: Brain,
      title: "Knowledge Loss",
      description: "Your best manager leaves? Their expertise leaves with them. Start from zero again.",
    },
    {
      icon: Target,
      title: "Scaling Breaks Things",
      description: "What works at $5K/month fails at $50K. Manual scaling is guesswork, not strategy.",
    },
    {
      icon: RefreshCcw,
      title: "Always Behind",
      description: "Platform algorithms change weekly. Manual optimization means you're always reacting, never leading.",
    },
  ];

  const iterationSteps = [
    {
      step: "01",
      icon: Zap,
      title: "Launch",
      description: "Deploy campaigns across all platforms from one strategy. AI configures everything automatically.",
      color: "blue",
    },
    {
      step: "02",
      icon: BarChart3,
      title: "Measure",
      description: "Real-time performance tracking across every platform. Every click, conversion, and dollar tracked.",
      color: "cyan",
    },
    {
      step: "03",
      icon: Brain,
      title: "Learn",
      description: "AI identifies patterns: winning creatives, headlines, audiences, platforms, and timing.",
      color: "purple",
    },
    {
      step: "04",
      icon: RefreshCcw,
      title: "Iterate",
      description: "Auto-generate variations, shift budgets, refine audiences. Launch improved version. Repeat.",
      color: "green",
    },
  ];

  const optimizationCategories = [
    {
      title: "Creative",
      items: [
        "New image/video variations",
        "Color and layout adjustments",
        "CTA button optimization",
        "Visual hook testing",
      ],
    },
    {
      title: "Copy",
      items: [
        "Headline variations",
        "Description rewrites",
        "Hook and angle testing",
        "Tone and voice optimization",
      ],
    },
    {
      title: "Audience",
      items: [
        "Winning segment expansion",
        "Poor performer exclusion",
        "Lookalike discovery",
        "Interest refinement",
      ],
    },
    {
      title: "Budget",
      items: [
        "Cross-platform reallocation",
        "Dayparting optimization",
        "Winner scaling",
        "Loser budget cuts",
      ],
    },
    {
      title: "Bidding",
      items: [
        "Strategy adjustments",
        "Bid cap optimization",
        "Conversion event testing",
        "ROAS target tuning",
      ],
    },
    {
      title: "Placement",
      items: [
        "Feed vs. Stories vs. Reels",
        "In-stream vs. in-feed",
        "Audience Network on/off",
        "Placement bid multipliers",
      ],
    },
    {
      title: "Seasonal Campaigns",
      icon: Calendar,
      items: [
        "Automatic holiday campaign generation",
        "Seasonal promotion scheduling",
        "Event-based ad variations",
        "Black Friday/Cyber Monday optimization",
      ],
    },
    {
      title: "Trend Response",
      icon: TrendingUp,
      items: [
        "Social media trend detection",
        "Viral topic campaign creation",
        "Real-time cultural moment adaptation",
        "Trending hashtag integration",
      ],
    },
  ];

  const strategySteps = [
    {
      icon: Target,
      title: "You Set the Strategy",
      description: "Define your brand voice, target audience, budget, and goals. Your expertise drives the direction.",
    },
    {
      icon: Brain,
      title: "AI Executes 24/7",
      description: "Once configured, the AI handles everything: creative generation, bid adjustments, budget allocation, and performance optimization.",
    },
    {
      icon: Zap,
      title: "Instant Adaptation",
      description: "When platforms change or performance shifts, the AI responds immediately—no manual intervention needed.",
    },
  ];

  const comparison = {
    manual: [
      "Set it and forget it (until it breaks)",
      "2-4 weeks to find winning creative",
      "60% of ads fatigue within 30 days",
      "Manual optimization = 10 hrs/week/campaign",
      "Platform changes break campaigns",
      "Knowledge leaves when staff leaves",
      "Reactive, always behind",
    ],
    leadspector: [
      "Set it and it gets better",
      "3-5 days to find winning creative",
      "<10% fatigue rate with auto-refresh",
      "AI optimization = 1 hr/week/campaign",
      "Adapts to algorithm changes automatically",
      "Knowledge encoded in strategy forever",
      "Proactive, always improving",
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <WaitlistDialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} />

      {/* Navigation */}
      <nav className="border-b border-slate-800 bg-slate-950/50 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link href="/" className="text-xl font-bold text-white">
            LeadSpector
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link href="#problems">
              <Button variant="ghost" className="text-slate-300">
                Problems
              </Button>
            </Link>
            <Link href="#how-it-works">
              <Button variant="ghost" className="text-slate-300">
                How It Works
              </Button>
            </Link>
            <Link href="#results">
              <Button variant="ghost" className="text-slate-300">
                Results
              </Button>
            </Link>
            {!isWaitlistMember && !isLoading && (
              <Button onClick={() => setIsWaitlistOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                Join Waitlist
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            {!isWaitlistMember && !isLoading && (
              <Button onClick={() => setIsWaitlistOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-sm px-4 hidden sm:flex">
                Join Waitlist
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-300"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800 bg-slate-950/95 backdrop-blur">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
              <Link
                href="#problems"
                className="py-3 text-slate-300 hover:text-white hover:bg-slate-800 px-4 rounded-md -mx-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Problems
              </Link>
              <Link
                href="#how-it-works"
                className="py-3 text-slate-300 hover:text-white hover:bg-slate-800 px-4 rounded-md -mx-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How It Works
              </Link>
              <Link
                href="#results"
                className="py-3 text-slate-300 hover:text-white hover:bg-slate-800 px-4 rounded-md -mx-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Results
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <div className="flex flex-col items-center gap-4">
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-sm px-4 py-2">
                <Sparkles className="h-4 w-4 mr-2" />
                Self-Improving Campaigns
              </Badge>
              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-sm px-4 py-2">
                Currently in Private Beta
              </Badge>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-tight">
              Campaigns That Get{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Smarter
              </span>{" "}
              While You Sleep
            </h1>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Launch once. The engine learns what works, then automatically iterates—
              new creatives, optimized copy, refined audiences. Your campaigns don&apos;t stagnate.
              They evolve. Every week, they&apos;re smarter than the last.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              {!isWaitlistMember || isLoading ? (
                <Button size="lg" onClick={() => setIsWaitlistOpen(true)} className="text-lg px-8 bg-blue-600 hover:bg-blue-700">
                  Join Waitlist
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              ) : (
                <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-sm px-4 py-2">
                  <Check className="h-4 w-4 mr-2" />
                  On Waitlist
                </Badge>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">58%</div>
                <div className="text-slate-400">Average CPA Improvement</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">3-5 Days</div>
                <div className="text-slate-400">To Find Winning Creative</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">&lt;10%</div>
                <div className="text-slate-400">Campaign Fatigue Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problems" className="py-24 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Old Way Is Broken
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Manual campaign management can&apos;t keep up with platform changes,
              creative fatigue, and competitive pressure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, i) => (
              <Card key={i} className="bg-slate-950 border-slate-800 hover:border-red-500/30 transition-colors">
                <CardHeader>
                  <problem.icon className="h-10 w-10 text-red-500 mb-4" />
                  <CardTitle className="text-white text-xl">{problem.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-400 text-base">
                    {problem.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Iteration Engine
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              A self-improving loop that learns, adapts, and optimizes 24/7.
            </p>
          </div>

          {/* Loop Visualization */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
              {iterationSteps.map((item, i) => (
                <div key={i} className="relative">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-slate-800 border-2 border-blue-500 flex items-center justify-center text-sm font-bold text-blue-400 z-10">
                    {item.step}
                  </div>

                  <Card className="bg-slate-950 border-slate-800 h-full hover:border-blue-500/50 transition-colors">
                    <CardHeader>
                      <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-4 ${
                        item.color === 'blue' ? 'bg-blue-500/10' :
                        item.color === 'cyan' ? 'bg-cyan-500/10' :
                        item.color === 'purple' ? 'bg-purple-500/10' :
                        'bg-green-500/10'
                      }`}>
                        <item.icon className={`h-7 w-7 ${
                          item.color === 'blue' ? 'text-blue-400' :
                          item.color === 'cyan' ? 'text-cyan-400' :
                          item.color === 'purple' ? 'text-purple-400' :
                          'text-green-400'
                        }`} />
                      </div>
                      <CardTitle className="text-white text-xl">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-slate-400 text-base">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* What Gets Iterated */}
          <div className="mt-24">
            <h3 className="text-2xl font-bold text-white text-center mb-12">
              What The Engine Optimizes Automatically
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {optimizationCategories.map((category, i) => (
                <Card key={i} className="bg-slate-950 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      {category.icon && <category.icon className="h-5 w-5 text-green-500" />}
                      {!category.icon && <Check className="h-5 w-5 text-green-500" />}
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="text-slate-400 text-sm flex items-start gap-2">
                          <span className="text-blue-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* You Set Strategy, AI Executes Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              You Set the Strategy. AI Executes.
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Your expertise drives the direction. Our AI handles the execution—24/7.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {strategySteps.map((item, i) => (
              <Card key={i} className="bg-slate-950 border-slate-800 hover:border-blue-500/50 transition-colors">
                <CardHeader>
                  <item.icon className="h-12 w-12 text-blue-400 mb-4" />
                  <CardTitle className="text-white text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-400 text-base">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Supported Ad Platforms
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Manage all your ad campaigns from one unified platform.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="flex flex-col items-center justify-center p-8 bg-slate-950 rounded-lg border border-slate-800 hover:border-blue-500/50 transition-colors">
              <img src="/meta.png" alt="Meta Ads" className="h-16 w-16 object-contain mb-4" />
              <span className="text-slate-300 font-medium">Meta Ads</span>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-950 rounded-lg border border-slate-800 hover:border-blue-500/50 transition-colors">
              <img src="/google.png" alt="Google Ads" className="h-16 w-16 object-contain mb-4" />
              <span className="text-slate-300 font-medium">Google Ads</span>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-950 rounded-lg border border-slate-800 hover:border-blue-500/50 transition-colors">
              <img src="/tiktok.png" alt="TikTok Ads" className="h-16 w-16 object-contain mb-4" />
              <span className="text-slate-300 font-medium">TikTok Ads</span>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-950 rounded-lg border border-slate-800 hover:border-blue-500/50 transition-colors">
              <img src="/reddit.png" alt="Reddit Ads" className="h-16 w-16 object-contain mb-4" />
              <span className="text-slate-300 font-medium">Reddit Ads</span>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-24 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Real Results From Self-Improving Campaigns
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              See what happens when campaigns evolve instead of stagnate.
            </p>
          </div>

          {/* More Stats */}
          <div className="grid gap-6 md:grid-cols-3 mt-12">
            <Card className="bg-slate-950 border-slate-800 text-center p-8">
              <div className="text-4xl font-bold text-blue-400 mb-2">10x</div>
              <div className="text-slate-400">Faster Creative Testing</div>
            </Card>
            <Card className="bg-slate-950 border-slate-800 text-center p-8">
              <div className="text-4xl font-bold text-cyan-400 mb-2">40-60%</div>
              <div className="text-slate-400">Monthly Performance Gain</div>
            </Card>
            <Card className="bg-slate-950 border-slate-800 text-center p-8">
              <div className="text-4xl font-bold text-purple-400 mb-2">90%</div>
              <div className="text-slate-400">Reduction in Manual Work</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Difference Is Clear
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Manual Management */}
            <Card className="bg-slate-950 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-2xl flex items-center gap-3">
                  <Clock className="h-7 w-7 text-red-500" />
                  Manual Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {comparison.manual.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-red-500 mt-1">✕</span>
                    <span className="text-slate-400">{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* LeadSpector Iteration */}
            <Card className="bg-slate-950 border-green-500/30">
              <CardHeader>
                <CardTitle className="text-white text-2xl flex items-center gap-3">
                  <Zap className="h-7 w-7 text-green-500" />
                  LeadSpector Iteration Engine
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {comparison.leadspector.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="text-green-500 mt-1" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Stop Managing.
            <br />
            Start <span className="text-blue-400">Iterating.</span>
          </h2>
          <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
            Your campaigns should get smarter every day. Not stagnate. Not break.
            Not require constant babysitting. Let the engine do the work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isWaitlistMember || isLoading ? (
              <Button size="lg" onClick={() => setIsWaitlistOpen(true)} className="text-lg px-8 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                Join the Waitlist
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-sm px-4 py-2 self-center">
                <Check className="h-4 w-4 mr-2" />
                On Waitlist
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-6">
            Join our private beta for early access.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
